import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { Notification } from 'components';
import { useIsProActivated, useSidebarState } from 'hooks';
import useAddress from 'hooks/use-address';
import useSafe from 'hooks/use-safe';
import { TransactionStatus, useAppsIframe, usePermissionList } from 'store';

import { useMenuList } from './util/useMenuList';
import { MenuItem } from './components';

type MenuListProps = {
  handleBreakIframe?: () => void;
};

export const MenuLst: React.FC<MenuListProps> = ({ handleBreakIframe }) => {
  const location = useLocation();
  const isInvite = location.pathname.startsWith('/invite/');

  const { safe, transactions } = useSafe();
  const walletAccount = useAddress();
  const { currentSafeId } = useSidebarState();
  const isProActivated = useIsProActivated();
  const { permissionList } = usePermissionList();

  const hasNotifications = useMemo(
    () =>
      transactions?.some((x) =>
        [TransactionStatus.QUEUED, TransactionStatus.EXECUTION].includes(x.status)
      ),
    [transactions]
  );
  const hasMessagesToSign = useMemo(
    () =>
      permissionList.some((signingMessage) => {
        if (!signingMessage.signatures || !signingMessage.signatures.length) {
          return true;
        }

        return !signingMessage.signatures.some((message) => message.userAddress === walletAccount);
      }),
    [permissionList, walletAccount]
  );

  const { selectedTab, setSelectedTab, menuOptions, menuInviteOptions } = useMenuList(
    safe._id || currentSafeId,
    safe.address
  );
  const { setSidebarHidden, setIframeRun } = useAppsIframe();

  useEffect(() => {
    const isLegacyPath = location.pathname.startsWith('/multisig');
    const transformedLegacyPath = `/vault/${safe.address}/${location.pathname
      .split('/')
      .slice(-1)}`;
    const newSelectedTab = menuOptions.find(
      (option) =>
        option.path === location.pathname || (isLegacyPath && option.path === transformedLegacyPath)
    );
    if (newSelectedTab) {
      setSelectedTab(newSelectedTab.key);
    } else if (isInvite) {
      setSelectedTab('owners.pending');
    }
  }, [location.pathname, menuOptions, safe]);

  const options = isInvite ? menuInviteOptions : menuOptions;

  return (
    <div className="flex flex-col w-full">
      {options.map((tab) => {
        const titleWithNotification =
          tab.key === 'transactions' && (hasNotifications || hasMessagesToSign) ? (
            <div className="flex items-center gap-[12px]">
              <span className="text-r13">{tab.title}</span>
              <Notification />
            </div>
          ) : (
            tab.title
          );

        return (
          <MenuItem
            {...tab}
            key={tab.key}
            title={titleWithNotification}
            isActive={selectedTab === tab.key}
            onClick={() => {
              if (selectedTab === tab.key && tab.key === 'stacking') {
                setIframeRun(true);
                setSidebarHidden(true);
              } else {
                handleBreakIframe && handleBreakIframe();
                setSelectedTab(tab.key);
              }
            }}
            disabled={tab.disabled || (!isInvite && !Boolean(safe.address))}
            isActivatedPro={isProActivated}
            stacking={selectedTab === tab.key && tab.key === 'stacking'}
          />
        );
      })}
    </div>
  );
};
